import React, { useEffect, useState } from "react";
import axios from "../api/baseurl";
import { useNavigate } from "react-router-dom";
import "./Spinner.css";
import "./Loader.css";
import { toast } from "react-toastify";
import { Button, Modal } from "react-bootstrap";
import { FaEdit, FaTrash } from "react-icons/fa";
import AddLabCoordinators from "./addLabCoordinators";
import "./hm-theme.css";

function LabCoordinators({ labcode }) {
  const [labDetails, setLabDetails] = useState(null);
  const [coordinators, setCoordinators] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCoordinator, setSelectedCoordinator] = useState(null);
  const [loader, setLoader] = useState(true);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    getData();
  }, [labcode]);

  const getData = async () => {
    const token = JSON.parse(localStorage.getItem("data"))?.token;
    setLoader(true);
    try {
      const labUrl = `/api/labdetails/labcode=${labcode}`;
      const labResponse = await axios.get(labUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      setLabDetails(labResponse.data);

      const coordinatorUrl = `/api/lab-coordinators/labId=${labResponse.data.id}`;
      const coordinatorResponse = await axios.get(coordinatorUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      setCoordinators(coordinatorResponse.data || []);
      setLoader(false);
    } catch (error) {
      setLoader(false);
      handleError(error);
    }
  };

  function handleError(error) {
    if (error.response) {
      if (error.response.status === 401) {
        navigate(`/signin`);
        toast.error("Your session has expired. Please login.");
      } else if (error.response.data && error.response.data.message) {
        toast.error(error.response.data.message);
      } else if (error.response.data && error.response.data.code) {
        toast.error(error.response.data.code);
      } else {
        toast.error("An error occurred.");
      }
    } else if (error.message) {
      toast.error(error.message);
    } else {
      toast.error("An error occurred.");
    }
  }

  const handleEditCoordinator = (coordinator) => {
    setSelectedCoordinator(coordinator);
    setIsModalOpen(true);
  };

  const handleAddCoordinator = () => {
    setSelectedCoordinator(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCoordinator(null);
    getData();
  };

  const handleDelete = (coordinator) => {
    setSelectedCoordinator(coordinator);
    setShowConfirmationModal(true);
  };

  const handleDeleteConfirm = async () => {
    const token = JSON.parse(localStorage.getItem("data"))?.token;
    const options = {
      headers: { Authorization: `Bearer ${token}` },
    };
    
    try {
      setLoader(true);
      const requestBody = {
        id: selectedCoordinator.id,
      };

      await axios.delete(`/api/lab-coordinators`, { data: requestBody, ...options });
      
      toast.success('Co-ordinator details deleted successfully!');
      setLoader(false);
      setShowConfirmationModal(false);
      setSelectedCoordinator(null);
      getData();
    } catch (error) {
      setLoader(false);
      handleError(error);
    }
  };

  return (
    <div>
      {loader && <div className="loading"></div>}
      
      <div className="hm-card">
        {/* Header */}
        <div className="hm-card-header">
          <h3 className="hm-card-title">
            <i className="material-icons">contact_phone</i>
            Co-ordinators Details
          </h3>
          <button className="btn-hm-primary" onClick={handleAddCoordinator}>
            <i className="material-icons" style={{ fontSize: "1rem" }}>person_add</i>
            Add Co-ordinator
          </button>
        </div>

        {/* Table */}
        <div className="hm-card-body p-0">
          <div style={{ overflowX: "auto" }}>
            <table className="hm-table">
              <thead>
                <tr>
                  <th>Co-ordinator Name</th>
                  <th>Email Address</th>
                  <th>Mobile Number</th>
                  <th style={{ width: "100px" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {coordinators.length === 0 ? (
                  <tr>
                    <td colSpan="4">
                      <div className="hm-empty-state">
                        <i className="material-icons">group_off</i>
                        <p>{loader ? "Loading co-ordinators..." : "No Data Available"}</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  coordinators.map((coord) => (
                    <tr key={coord.id}>
                      <td className="fw-semibold text-dark">{coord.coOrdinatorName}</td>
                      <td>{coord.coOrdinatorEmail}</td>
                      <td>{coord.coOrdinatorMobileno}</td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <button
                            type="button"
                            className="btn btn-outline-warning btn-sm rounded-circle p-1 d-inline-flex align-items-center justify-content-center"
                            style={{ width: "32px", height: "32px" }}
                            title="Edit Co-ordinator"
                            onClick={() => handleEditCoordinator(coord)}
                          >
                            <FaEdit size={14} />
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm rounded-circle p-1 d-inline-flex align-items-center justify-content-center"
                            style={{ width: "32px", height: "32px" }}
                            title="Delete Co-ordinator"
                            onClick={() => handleDelete(coord)}
                          >
                            <FaTrash size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {isModalOpen && (
        <AddLabCoordinators
          isOpen={isModalOpen}
          onRequestClose={handleCloseModal}
          labId={labDetails?.id} 
          coOrdinator={selectedCoordinator}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Modal show={showConfirmationModal} onHide={() => setShowConfirmationModal(false)} centered className="hm-modal">
        <Modal.Header closeButton className="hm-card-header">
          <Modal.Title className="hm-card-title text-danger">
            <i className="material-icons text-danger">warning</i>
            Confirm Deletion
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <p className="mb-0 text-secondary">
            Are you sure you want to delete co-ordinator <strong className="text-dark">{selectedCoordinator?.coOrdinatorName}</strong>? This action cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 pb-4 px-4 d-flex justify-content-end gap-2">
          <Button variant="secondary" className="rounded-pill px-4" onClick={() => setShowConfirmationModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" className="rounded-pill px-4" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default LabCoordinators;

